import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import EditLocationInfoModal, { LocationInfoData } from '../../../components/EditLocationInfoModal';
import EditLocationHoursModal from '../../../components/EditLocationHoursModal';
import ContactFormModal from '../../../components/ContactFormModal';
import DeleteContactModal from '../../../components/DeleteContactModal';
import { useBrandContext } from '../../../contexts/BrandContext';
import { PAGE_ADD_LOCATION, PAGE_MENUS } from '../../../constants/UriConstants';
import { EditIcon, TrashCanIcon } from '../../../assets/svgs/icons';
import { getRestaurantsByBrandID } from '../../../api/brand';
import LocationMenusButton from '../../../components/LocationMenusButton';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { deleteRestaurant } from '../../../api/restaurant';

// ─── Types ───────────────────────────────────────────────────────────────────

interface HoursEntry {
  days: boolean[];
  startTime: string;
  endTime: string;
}

interface Location {
  id: number;
  name: string;
  address: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
  email: string;
  phone: string;
  /** null when the API doesn't report it (older backend): both menu buttons are shown. */
  menuCount: number | null;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const TH_CLASS = 'text-left py-3 text-xs tracking-wider text-[var(--color-text-secondary)] font-normal';

const EDITABLE_CELL_CLASS = 'group relative cursor-pointer rounded px-1 py-0.5 hover:bg-black/5 transition-colors';

// ─── Component ───────────────────────────────────────────────────────────────

const LocationsPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { currentBrand, setBrand } = useBrandContext();
  const { setRestaurant } = useRestaurantContext();

  // Locations from API
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Modal states
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editHoursOpen, setEditHoursOpen] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [deleteLocationOpen, setDeleteLocationOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);

  // Sync brand from URL param
  useEffect(() => {
    if (brandId && currentBrand?.id !== brandId) {
      setBrand(brandId);
    }
  }, [brandId, currentBrand?.id, setBrand]);

  // Fetch locations when brand changes
  useEffect(() => {
    const fetchLocations = async () => {
      if (!currentBrand?.id) return;
      try {
        setIsLoadingLocations(true);
        const data = await getRestaurantsByBrandID(currentBrand.id);
        const mapped: Location[] = data.map((r: any) => ({
          id: r.restaurant_id,
          name: r.name || '',
          address: r.address || '',
          address2: r.address2 || null,
          city: r.city || '',
          state: r.state || '',
          zip: r.zip || '',
          email: (r.email || '').trim(),
          phone: r.phone || '',
          menuCount: typeof r.menu_count === 'number' ? r.menu_count : null
        }));
        setLocations(mapped);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [currentBrand?.id]);

  // ─── Button Labels ───────────────────────────────────────────────────
  // const getStripeButtonLabel = () => {
  //   if (isLoadingStripe) return 'LOADING...';
  //   if (isStripeConnected) return 'UPDATE';
  //   return 'CONNECT';
  // };

  // ─── Helpers ─────────────────────────────────────────────────────────

  const formatAddress = (location: Location) => {
    const street = [location.address, location.address2].filter(Boolean).join(' ');
    const cityStateZip = [location.city, location.state, location.zip].filter(Boolean).join(', ');
    return [street, cityStateZip].filter(Boolean).join('\n');
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '';
    // Format 10-digit number as (XXX) XXX-XXXX
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleEditInfo = (location: Location) => {
    setEditingLocation(location);
    setEditInfoOpen(true);
  };

  const handleDeleteLocation = (location: Location) => {
    setEditingLocation(location);
    setDeleteLocationOpen(true);
  };

  const handleSaveInfo = (data: LocationInfoData) => {
    setEditInfoOpen(false);
    setEditingLocation(null);
  };

  const handleSaveHours = (entries: HoursEntry[]) => {
    setEditHoursOpen(false);
    setEditingLocation(null);
  };

  const handleSaveContact = (data: {
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    email: string;
  }) => {
    setEditContactOpen(false);
    setEditingLocation(null);
  };

  const handleConfirmDeleteLocation = async () => {
    if (!editingLocation) {
      return;
    }

    try {
      await deleteRestaurant(editingLocation.id);

      setLocations((currentLocations) => currentLocations.filter((location) => location.id !== editingLocation.id));

      setDeleteLocationOpen(false);
      setEditingLocation(null);
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  const closeModals = () => {
    setEditInfoOpen(false);
    setEditHoursOpen(false);
    setEditContactOpen(false);
    setDeleteLocationOpen(false);
    setEditingLocation(null);
  };

  const getInfoData = (): LocationInfoData | null => {
    if (!editingLocation) return null;
    return {
      locationName: editingLocation.name,
      streetAddress1: editingLocation.address,
      streetAddress2: editingLocation.address2 || '',
      city: editingLocation.city,
      state: editingLocation.state,
      zip: editingLocation.zip,
      phone: editingLocation.phone,
      email: editingLocation.email,
      website: ''
    };
  };

  const getContactData = (): null => null;

  const getHoursData = (): HoursEntry[] | null => null;

  const handleShowLocation = (locationId: number) => {
    setRestaurant(locationId);
    navigate(`/location-profile/${locationId}`);
  };

  const handleAddMenu = (locationId: number) => {
    setRestaurant(locationId);
    navigate('/create-menu');
  };

  const handleEditMenus = (locationId: number) => {
    setRestaurant(locationId);
    navigate(`/${locationId}/${PAGE_MENUS}`);
  };

  const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') callback();
  };

  // ─── Render ──────────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader title="LOCATIONS">
        <button
          type="button"
          className="px-8 py-3 rounded-full text-xs text-white transition-colors"
          style={{ backgroundColor: 'var(--color-accent)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
          }}
          onClick={() => navigate(`/${currentBrand?.id}/${PAGE_ADD_LOCATION}`)}
        >
          ADD LOCATION
        </button>
      </PageHeader>

      <div style={{ padding: '24px 50px' }}>
        {isLoadingLocations ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
            <LoadingSpinner />
          </div>
        ) : (
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '32px' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '40px' }} />
            </colgroup>
            <thead>
              <tr className="border-b border-[var(--color-border-light)]">
                <th aria-label="Drag" />
                <th className={TH_CLASS}>NAME</th>
                <th className={TH_CLASS}>ADDRESS</th>
                <th className={TH_CLASS}>EMAIL/PHONE</th>
                <th className={TH_CLASS}>MENUS</th>
                <th aria-label="View" />
                <th aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 ? (
                <tr>
                  <td />
                  <td colSpan={6} className="py-8 text-center text-[var(--color-text-muted)]">
                    No locations found for this brand.
                  </td>
                </tr>
              ) : (
                locations.map((location) => (
                  <tr
                    key={location.id}
                    className="border-b border-[var(--color-border-light)]"
                    style={{ verticalAlign: 'top' }}
                  >
                    {/* Drag Handle */}
                    <td className="py-4">
                      <span
                        style={{
                          color: 'var(--color-text-muted)',
                          cursor: 'grab',
                          fontSize: '16px'
                        }}
                      >
                        ☰
                      </span>
                    </td>

                    {/* Name */}
                    <td className="py-4">{location.name}</td>

                    {/* Address */}
                    <td className="py-4">
                      <div
                        className={`${EDITABLE_CELL_CLASS} whitespace-pre-line`}
                        onClick={() => handleEditInfo(location)}
                        onKeyDown={handleKeyDown(() => handleEditInfo(location))}
                        role="button"
                        tabIndex={0}
                      >
                        {formatAddress(location) || '--'}
                        <span className="hidden group-hover:flex absolute top-1 right-1 opacity-40">
                          <EditIcon />
                        </span>
                      </div>
                    </td>

                    {/* Email / Phone */}
                    <td className="py-4">
                      <div
                        className={EDITABLE_CELL_CLASS}
                        onClick={() => handleEditInfo(location)}
                        onKeyDown={handleKeyDown(() => handleEditInfo(location))}
                        role="button"
                        tabIndex={0}
                      >
                        {location.phone || location.email ? (
                          <>
                            {location.phone && <div>{formatPhone(location.phone)}</div>}
                            {location.email && <div>{location.email}</div>}
                          </>
                        ) : (
                          <span className="text-[var(--color-text-muted)]">--</span>
                        )}
                        <span className="hidden group-hover:flex absolute top-1 right-1 opacity-40">
                          <EditIcon />
                        </span>
                      </div>
                    </td>

                    {/* Menus — ADD MENUS when the location has none, EDIT MENUS when it has some */}
                    <td className="py-4">
                      <LocationMenusButton
                        menuCount={location.menuCount}
                        onAddMenu={() => handleAddMenu(location.id)}
                        onEditMenus={() => handleEditMenus(location.id)}
                      />
                    </td>

                    {/* View Location */}
                    <td className="py-4" style={{ verticalAlign: 'top' }}>
                      <button
                        type="button"
                        className="px-4 py-1.5 rounded-full text-xs text-white transition-colors whitespace-nowrap"
                        style={{
                          backgroundColor: 'var(--color-primary-blue)',
                          width: 'fit-content'
                        }}
                        onClick={() => handleShowLocation(location.id)}
                      >
                        VIEW LOCATION
                      </button>
                    </td>

                    {/* Delete */}
                    <td className="py-4" style={{ verticalAlign: 'top' }}>
                      <button
                        type="button"
                        aria-label="Delete location"
                        onClick={() => handleDeleteLocation(location)}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--color-danger)',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <span
                          style={{
                            filter: 'brightness(0) invert(1)',
                            display: 'flex',
                            pointerEvents: 'none'
                          }}
                        >
                          <TrashCanIcon />
                        </span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Basic Info Modal */}
      <EditLocationInfoModal
        isOpen={editInfoOpen}
        data={getInfoData()}
        onCancel={closeModals}
        onSave={handleSaveInfo}
      />

      {/* Edit Hours Modal */}
      <EditLocationHoursModal
        isOpen={editHoursOpen}
        data={getHoursData()}
        onCancel={closeModals}
        onSave={handleSaveHours}
      />

      {/* Edit Contact Modal */}
      <ContactFormModal
        isOpen={editContactOpen}
        contact={getContactData()}
        onCancel={closeModals}
        onSubmit={handleSaveContact}
      />

      {/* Delete Location Modal */}
      <DeleteContactModal
        isOpen={deleteLocationOpen}
        title="DELETE LOCATION"
        itemName="location"
        onCancel={closeModals}
        onConfirm={handleConfirmDeleteLocation}
      />
    </div>
  );
};

export default LocationsPage;
